import { useEffect, useRef, useCallback, useMemo } from "react";
import type {
  AssetProps,
  AssetError,
  AssetEvent,
  FetchAssetFilter,
} from "../types";
import { useAssetRenderer } from "../hooks/useAssetRenderer";
export interface AssetRendererProps extends Omit<
  AssetProps,
  "authenticationToken"
> {
  authenticationToken: string | null;
  env?: string;
  fallback?: React.ReactNode;
  errorFallback?: (error: string) => React.ReactNode;
  authRequiredMessage?: string;
  webViewLoadingText?: string;
  webViewErrorText?: string;
  onSubmitQuestion?: (response: unknown) => void;
  revision?: number;
  prefetchAssetIds?: string[];
  prefetchFilters?: FetchAssetFilter;
  prefetchStaleTime?: number;
}

/**
 * Renders the remote Asset component (React 18) inside its own root.
 * The remote bundles its own React 18; our host runs React 19.
 * Two React versions can't share a tree, so we bridge via a DOM node.
 */
export function AssetRenderer({
  fallback = null,
  errorFallback,
  authRequiredMessage,
  authenticationToken,
  env,
  onError,
  onEvent,
  onSubmitQuestion,
  revision,
  prefetchAssetIds,
  prefetchFilters,
  prefetchStaleTime,
  webViewLoadingText,
  webViewErrorText,
  ...assetProps
}: AssetRendererProps) {
  const { AssetComponent, remoteReact, remoteReactDOM, isLoading, error } =
    useAssetRenderer(env);
  const containerRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<unknown>(null);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const handleError = useCallback((err: AssetError) => {
    onErrorRef.current?.(err);
  }, []);

  const handleEvent = useCallback((event: AssetEvent) => {
    onEventRef.current?.(event);
  }, []);

  const remotePropsKey = useMemo(
    () =>
      JSON.stringify({
        assetId: assetProps.assetId,
        assetType: assetProps.assetType,
        themeName: assetProps.themeName,
        colorScheme: assetProps.colorScheme,
        assetTypeProps: assetProps.assetTypeProps,
        fetchAssetFilter: assetProps.fetchAssetFilter,
        authenticationToken,
      }),
    [
      assetProps.assetId,
      assetProps.assetType,
      assetProps.themeName,
      assetProps.colorScheme,
      assetProps.assetTypeProps,
      assetProps.fetchAssetFilter,
      authenticationToken,
    ],
  );

  // Store latest assetProps in a ref so the effect doesn't depend on the spread object
  const assetPropsRef = useRef(assetProps);
  assetPropsRef.current = assetProps;
  const authTokenRef = useRef(authenticationToken);
  authTokenRef.current = authenticationToken;

  useEffect(() => {
    if (
      !AssetComponent ||
      !remoteReact ||
      !remoteReactDOM ||
      !containerRef.current ||
      !authTokenRef.current
    )
      return;

    const React18 = remoteReact;
    const ReactDOM18 = remoteReactDOM;

    if (!rootRef.current && ReactDOM18.createRoot) {
      rootRef.current = ReactDOM18.createRoot(containerRef.current);
    }

    const element = React18.createElement(AssetComponent, {
      ...assetPropsRef.current,
      authenticationToken: authTokenRef.current,
      onError: handleError,
      onEvent: handleEvent,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const root = rootRef.current as any;
    if (root) {
      root.render(element);
    }
  }, [
    AssetComponent,
    remoteReact,
    remoteReactDOM,
    remotePropsKey,
    handleError,
    handleEvent,
  ]);

  useEffect(() => {
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const root = rootRef.current as any;
      if (root) {
        root.unmount();
        rootRef.current = null;
      }
    };
  }, []);

  if (isLoading) return <>{fallback}</>;
  if (error) return <>{errorFallback ? errorFallback(error) : null}</>;
  if (!AssetComponent) return null;
  if (!authenticationToken) {
    return (
      <>{errorFallback ? errorFallback(authRequiredMessage ?? "") : null}</>
    );
  }

  return <div ref={containerRef} style={{ width: "100%", height: "100%" }} />;
}
